import { Form, FormField } from "@babackapp/ui/components/form";
import { Card, CardContent } from "@babackapp/ui/components/card";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormInputRow from "@babackapp/react-hook-forms-components/form-input-row";
import FormMultiOptionsSelector from "@babackapp/react-hook-forms-components/form-multi-options-selector";
import FormOptionsSelector from "@babackapp/react-hook-forms-components/form-option-selector";
import { SettingsFormPageButtonBar } from "@babackapp/react-hook-forms-components/form-page";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import FormTagsInput from "@babackapp/react-hook-forms-components/form-tags-input";
import useTranslation from "@babackapp/translations/use-translation";
import MetafieldForm from "./metafield-form.tsx";
import {
  type CustomerPortalFormValues,
  useCustomerPortalBasicsForm,
} from "@/pages/(app)/customer-portal/components/customer-portal-basics/use-customer-portal-basics-form.ts";

import { useShopFlag } from "@/hooks/use-flag.ts";
import ShopLocationSelect from "@/components/shop-location-select/shop-location-select";
import useShopifyShopLocations from "@babackapp/private-api-client/client/use-shopify-shop-locations";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { Link } from "react-router-dom";
import { OverrideRulesManager } from "@/components/override-rules";
import { LockClosedIcon } from "@heroicons/react/24/solid";

type CustomerPortalBasicsFormProps = {
  onSubmit: (data: CustomerPortalFormValues) => void;
  workflowLogisticianActivated: boolean;
  defaultValues: CustomerPortalFormValues;
  children?: React.ReactNode;
  isRetractionOnly?: boolean;
};

export default function CustomerPortalBasicsForm({
  onSubmit,
  defaultValues,
  children,
  isRetractionOnly,
}: CustomerPortalBasicsFormProps) {
  const t = useTranslation();
  const form = useCustomerPortalBasicsForm({
    defaultValues: defaultValues,
  });
  const isCarrierTagConditionEnabled = useShopFlag(
    "isCarrierTagConditionEnabled",
  );

  const isSingleArticleReturnOnly = form.watch("singleArticleReturnOnly");
  const isGroupItemsByFulfillment = form.watch("groupItemsByFulfillment");

  // For restock location selection
  const { data: shop } = useCurrentShopSuspense();
  const { data: locations } = useShopifyShopLocations(shop.id);

  const refundInitialShippingOptions = [
    {
      label: t("settings.config.firstLineItem"),
      key: "firstLineItem",
    },
    {
      label: t("settings.config.lastLineItem"),
      key: "lastLineItem",
    },
    { label: t("settings.config.never"), key: "never" },
  ];

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col justify-start gap-4"
      >
        <Card className="w-full">
          <CardContent className="flex flex-col justify-start gap-8 pt-6">
            <FormField
              name="contactLink"
              render={({ field }) => (
                <FormInput
                  label={t(
                    "settings.customerPortal.basics.fields.contactLink.title",
                  )}
                  description={t(
                    "settings.customerPortal.basics.fields.contactLink.description",
                  )}
                  {...field}
                />
              )}
            />

            <FormField
              name="languages"
              render={({ field }) => (
                <FormMultiOptionsSelector
                  hideKey
                  label={t(
                    "settings.customerPortal.basics.fields.languages.title",
                  )}
                  description={t(
                    "settings.customerPortal.basics.fields.languages.description",
                  )}
                  options={availableLanguages}
                  values={field.value ?? []}
                  onChange={(values) => field.onChange(values)}
                />
              )}
            />

            {!isRetractionOnly && (
              <FormField
                name="webOrderOnly"
                render={({ field }) => (
                  <FormSwitch
                    label={t(
                      "settings.customerPortal.basics.fields.webOrderOnly.title",
                    )}
                    description={t(
                      "settings.customerPortal.basics.fields.webOrderOnly.description",
                    )}
                    {...field}
                  />
                )}
              />
            )}

            {!isRetractionOnly && (
              <MetafieldForm
                name={"nonReturnableMetafieldItems"}
                label={t("settings.config.tagsForMetafieldItems.title")}
                description={t(
                  "settings.config.tagsForMetafieldItems.description",
                )}
              />
            )}

            {!isRetractionOnly && (
              <FormField
                name="tagsForNotAcceptedOrder"
                render={({ field }) => (
                  <FormTagsInput
                    label={t(
                      "settings.customerPortal.basics.fields.tagsForNotAcceptedOrder.title",
                    )}
                    description={t(
                      "settings.customerPortal.basics.fields.tagsForNotAcceptedOrder.description",
                    )}
                    {...field}
                  />
                )}
              />
            )}

            {isRetractionOnly && (
              <FormField
                name="nonReturnableItems"
                render={({ field }) => (
                  <FormTagsInput
                    label={t(
                      "settings.customerPortal.basics.fields.nonReturnableItems.title",
                    )}
                    description={t(
                      "settings.customerPortal.basics.fields.nonReturnableItems.description",
                    )}
                    {...field}
                  />
                )}
              />
            )}

            {isRetractionOnly ? (
              <div className="relative overflow-hidden rounded-lg border min-h-[160px]">
                <div className="pointer-events-none select-none p-6 space-y-4">
                  {/* Commandes web uniquement */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium">
                        {t(
                          "settings.customerPortal.basics.fields.webOrderOnly.title",
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t(
                          "settings.customerPortal.basics.fields.webOrderOnly.description",
                        )}
                      </div>
                    </div>
                    <div className="h-5 w-9 rounded-full bg-muted border" />
                  </div>
                  {/* Tags avec metafields */}
                  <div className="space-y-1.5">
                    <div className="text-sm font-medium">
                      {t("settings.config.tagsForMetafieldItems.title")}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("settings.config.tagsForMetafieldItems.description")}
                    </div>
                    <div className="flex gap-1 mt-1">
                      <div className="h-6 w-24 rounded-full bg-muted border" />
                    </div>
                  </div>
                  {/* Tags pour commandes non acceptées */}
                  <div className="space-y-1.5">
                    <div className="text-sm font-medium">
                      {t(
                        "settings.customerPortal.basics.fields.tagsForNotAcceptedOrder.title",
                      )}
                    </div>
                    <div className="flex gap-1">
                      <div className="h-6 w-20 rounded-full bg-muted border" />
                      <div className="h-6 w-28 rounded-full bg-muted border" />
                    </div>
                  </div>
                  {/* Réapprovisionnement */}
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">
                      {t(
                        "settings.customerPortal.basics.fields.restockOnReturn.title",
                      )}
                    </div>
                    <div className="h-5 w-9 rounded-full bg-muted border" />
                  </div>
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/70 backdrop-blur-[1.2px] px-6 text-center">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                    <LockClosedIcon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">
                    Options avancées du portail
                  </p>
                  <p className="text-xs text-muted-foreground max-w-sm">
                    Configurez les règles de retour, le réapprovisionnement, les
                    remboursements de frais de port et les options avancées de
                    votre portail client.
                  </p>
                  <Link
                    to={`/shops/${shop.id}/settings/billing`}
                    className="flex items-center gap-1.5 rounded-full bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white shadow-sm"
                  >
                    <LockClosedIcon className="h-3 w-3" />
                    Passer au plan Lite
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <FormField
                  name="nonReturnableItems"
                  render={({ field }) => (
                    <FormTagsInput
                      label={t(
                        "settings.customerPortal.basics.fields.nonReturnableItems.title",
                      )}
                      description={t(
                        "settings.customerPortal.basics.fields.nonReturnableItems.description",
                      )}
                      {...field}
                    />
                  )}
                />
                <OverrideRulesManager
                  actionType={"OVERRIDE_NON_RETURNABLE"}
                  title={t("overrideRules.nonReturnable.title")}
                  description={t("overrideRules.nonReturnable.description")}
                  excludeFields={[
                    "returnCreatedAt",
                    "returnItemCount",
                    "returnItemResolutions",
                    "returnReasonIds",
                  ]}
                  labels={{
                    rulesTitle: t("overrideRules.nonReturnable.rulesTitle"),
                    addRule: t("overrideRules.nonReturnable.addRule"),
                    createTitle: t("overrideRules.nonReturnable.createTitle"),
                    editTitle: t("overrideRules.nonReturnable.editTitle"),
                    deleteRule: t("overrideRules.nonReturnable.deleteRule"),
                  }}
                />

                <FormInputRow>
                  <FormField
                    name="restockOnReturn"
                    render={({ field }) => (
                      <FormSwitch
                        label={t(
                          "settings.customerPortal.basics.fields.restockOnReturn.title",
                        )}
                        description={t(
                          "settings.customerPortal.basics.fields.restockOnReturn.description",
                        )}
                        {...field}
                      />
                    )}
                  />
                </FormInputRow>

                {form.getValues().restockOnReturn && (
                  <FormField
                    name="restockLocationId"
                    render={({ field: locationField }) => {
                      const selectedLocation =
                        locations?.find((l) => l.id === locationField.value) ??
                        null;
                      return (
                        <ShopLocationSelect
                          value={selectedLocation}
                          onChange={(location) =>
                            locationField.onChange(location?.id ?? null)
                          }
                          allowNone={false}
                          description={t(
                            "settings.customerPortal.basics.fields.restockLocation.description",
                          )}
                        />
                      );
                    }}
                  />
                )}

                <FormField
                  name="clientCanUpdatePersonalInformation"
                  render={({ field }) => (
                    <FormSwitch
                      label={t(
                        "settings.customerPortal.basics.fields.clientCanUpdatePersonalInformation.title",
                      )}
                      description={t(
                        "settings.customerPortal.basics.fields.clientCanUpdatePersonalInformation.description",
                      )}
                      {...field}
                    />
                  )}
                />
                <div>
                  <FormField
                    name="refundInitialShippingRule"
                    render={({ field }) => (
                      <FormOptionsSelector
                        label={t("settings.config.refundInitialShippingRule")}
                        description={t(
                          "settings.config.refundInitialShippingRuleDescription",
                        )}
                        options={refundInitialShippingOptions}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <OverrideRulesManager
                    actionType={"OVERRIDE_SHIPPING_REFUND"}
                    actionValueOptions={refundInitialShippingOptions}
                  />
                </div>
                <div>
                  <FormField
                    name="refundInitialShippingAmountRule"
                    render={({ field }) => (
                      <>
                        <FormSwitch
                          label={t(
                            "settings.customerPortal.basics.fields.refundInitialShippingAmountRule.title",
                          )}
                          description={t(
                            "settings.customerPortal.basics.fields.refundInitialShippingAmountRule.description",
                          )}
                          {...field}
                          value={field.value >= 0}
                          onChange={(value) => field.onChange(value ? 0 : -1)}
                        />
                        {field.value >= 0 && (
                          <FormInput
                            type="number"
                            label=""
                            {...field}
                            onChange={(e) =>
                              field.onChange(
                                Number.isNaN(+e.target?.value)
                                  ? 0
                                  : +e.target?.value,
                              )
                            }
                          />
                        )}
                      </>
                    )}
                  />
                  <OverrideRulesManager
                    actionType={"OVERRIDE_INITIAL_SHIPPING_AMOUNT"}
                    excludeFields={[
                      "returnCreatedAt",
                      "returnItemCount",
                      "returnItemResolutions",
                      "returnReasonIds",
                    ]}
                  />
                </div>
                <FormField
                  name="oneResolutionPerReturn"
                  render={({ field }) => (
                    <FormSwitch
                      label={t("settings.shop.oneResolutionPerReturn.title")}
                      description={t(
                        "settings.shop.oneResolutionPerReturn.description",
                      )}
                      {...field}
                    />
                  )}
                />

                {isCarrierTagConditionEnabled && (
                  <FormField
                    name="singleArticleReturnOnly"
                    render={({ field }) => (
                      <FormSwitch
                        label={t("settings.shop.singleArticleReturnOnly.title")}
                        description={t(
                          "settings.shop.singleArticleReturnOnly.description",
                        )}
                        {...field}
                        value={Boolean(field.value)}
                      />
                    )}
                  />
                )}

                <div>
                  <FormField
                    name="groupItemsByFulfillment"
                    render={({ field }) => (
                      <FormSwitch
                        label={t("settings.shop.groupItemsByFulfillment.title")}
                        description={t(
                          "settings.shop.groupItemsByFulfillment.description",
                        )}
                        {...field}
                        value={Boolean(field.value)}
                      />
                    )}
                  />
                  {isSingleArticleReturnOnly && isGroupItemsByFulfillment && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <span>⚠</span>
                      {t(
                        "settings.shop.groupItemsByFulfillment.singleArticleNote",
                      )}
                    </p>
                  )}
                </div>

                <FormInputRow>
                  <FormField
                    name="canCancelReturnRequest"
                    render={({ field }) => (
                      <FormSwitch
                        label={t(
                          "settings.customerPortal.basics.fields.canCancelReturnRequest.title",
                        )}
                        description={t(
                          "settings.customerPortal.basics.fields.canCancelReturnRequest.description",
                        )}
                        {...field}
                      />
                    )}
                  />
                </FormInputRow>

                <FormInputRow>
                  <FormField
                    name="allowOnlyUniqueReturnRequest"
                    render={({ field }) => (
                      <FormSwitch
                        label={t(
                          "settings.customerPortal.basics.fields.allowOnlyUniqueReturnRequest.title",
                        )}
                        description={t(
                          "settings.customerPortal.basics.fields.allowOnlyUniqueReturnRequest.description",
                        )}
                        {...field}
                      />
                    )}
                  />
                </FormInputRow>
              </>
            )}
          </CardContent>
        </Card>

        {children}

        <SettingsFormPageButtonBar />
      </form>
    </Form>
  );
}

const availableLanguages = [
  { key: "fr-FR", label: "Français" },
  { key: "en-US", label: "English" },
  { key: "de-DE", label: "Deutsch" },
  { key: "it-IT", label: "Italiano" },
  { key: "es-ES", label: "Spanish" },
  { key: "nl-NL", label: "Nederlands" },
  { key: "ro-RO", label: "Romanian" },
  { key: "cs-CZ", label: "Czech" },
  { key: "da-DK", label: "Danish" },
  { key: "fi-FI", label: "Finnish" },
  { key: "el-GR", label: "Greek" },
  { key: "lv-LV", label: "Latvian" },
  { key: "sv-SE", label: "Swedish" },
  { key: "no-NO", label: "Norwegian" },
  { key: "pl-PL", label: "Polish" },
  { key: "ko-KR", label: "Korean" },
  { key: "ja-JP", label: "Japanese" },
  { key: "pt-BR", label: "Portuguese (Brazil)" },
  { key: "pt-PT", label: "Portuguese (Portugal)" },
  { key: "zh-Hans", label: "Chinese (Simplified)" },
  { key: "zh-Hant", label: "Chinese (Traditional)" },
];
